<template>
  <AdvancedDropdown
    v-model="modelValue"
    v-bind="$attrs"
    :options="options"
    :disabled="isCompanyRoleName('Broker') || disabled"
    :can-add-new="false"
    label="Broker"
    size="sm"
  >
    <template v-slot:button-content>
      <template v-if="!!selectedOptions.length">
        {{ selectedOptions.join(', ') }}
      </template>
      <template v-else>Select Broker</template>
    </template>

    <template v-slot:item-extra="item">
      <a
        v-if="item.isNew"
        :class="{
          'text-light': modelValue.includes(item.value),
          'text-danger': !modelValue.includes(item.value)
        }"
        role="button"
        @click.prevent.stop="() => handleRemoveNewBroker(item)"
      >
        <BaseIcon icon="x" tag="i" />
      </a>
    </template>

    <template v-if="!isSuperUser" v-slot:add-new="{ cancel }">
      <BDXNewBrokerForm
        size="sm"
        @cancel="cancel"
        @success="(newBroker) => handleNewBrokerFormSuccess(newBroker, cancel)"
      />
    </template>
  </AdvancedDropdown>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api'

import {
  useCurrentUser,
  useUserCompany,
  useRLSGroup
} from '@composables'

import { BaseIcon } from '@components/base'
import { AdvancedDropdown } from '@components/advanced'
import { BDXNewBrokerForm } from '@components/bdx'
import { KEEP_AS_IS_OPTION } from '@constants'

export default defineComponent({
  name: 'BDXEditFormBroker',
  components: {
    BaseIcon,

    AdvancedDropdown,

    BDXNewBrokerForm
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    useKeepAsIsOption: Boolean,
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'created:tpa', 'removed:tpa'],
  setup(props, { emit }) {
    const { isSuperUser } = useCurrentUser()
    const { isRoleName: isCompanyRoleName } = useUserCompany()
    const { isFetching, rlsGroupItems, addRLSGroupItem, removeRLSGroupItem } =
      useRLSGroup('Broker')

    const modelValue = computed({
      get: () => props.value,
      set: (newValue) => emit('input', newValue)
    })

    const options = computed(() => {
      let options = rlsGroupItems.value
        .filter((rlsGroupItem) => {
          if (isSuperUser.value || rlsGroupItem.isNew) return true

          // Companies of the same type as the signed-in user are excluded;
          // every other type is selectable without requiring an RLS relationship.
          return !isCompanyRoleName('Broker')
        })
        .map((rlsGroupItem) => ({
          text: rlsGroupItem.broker,
          value: rlsGroupItem.id,
          ...(rlsGroupItem.isNew && { isNew: true })
        }))
      if (props.useKeepAsIsOption) {
        options = [KEEP_AS_IS_OPTION, ...options];
      }
      return options;
    }
      
    )

    const selectedOptions = computed(() =>
      options.value
        .filter((option) => modelValue.value.includes(option.value))
        .map((option) => option.text)
    )

    // New Broker form
    const handleRemoveNewBroker = (Broker) => {
      removeRLSGroupItem(Broker.value)

      emit('removed:broker', Broker)
    }

    const handleNewBrokerFormSuccess = (newBroker, successCallback) => {
      if (isSuperUser.value) return

      addRLSGroupItem(newBroker)

      emit('created:broker', newBroker)

      successCallback()
    }

    return {
      isSuperUser,
      isCompanyRoleName,

      modelValue,

      isFetching,
      options,
      selectedOptions,

      handleRemoveNewBroker,
      handleNewBrokerFormSuccess
    }
  }
})
</script>

<style scoped>
.advanced-dropdown >>> .dropdown-toggle {
  max-width: 214px;
}

.advanced-dropdown >>> .dropdown-menu .list-group-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.advanced-dropdown >>> .dropdown-menu .list-group-item.disabled {
  color: #fff;
  background-color: #1c4a50;
  border-color: #1c4a50;
  pointer-events: initial;
}
</style>
